import { FormProvider, useForm } from 'react-hook-form';
import { GetDocumentRes, DocumentFormAction } from '@dealstack/shared/lib/generated';
import {
	Header,
	HeaderCloseBtn,
	HeaderTitle,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/SidePanels/styled';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { DocumentFieldView } from '@dealstack/frontend/shared/components/Documents/FormFields/DocumentFieldView/DocumentFieldView';
import {
	RightPanelAction,
	SelectedDocument,
} from '@dealstack/frontend/app/pages/Project/DocumentLibrary/types';
import { RightPanel } from '@dealstack/frontend/shared/layout/DashboardGrid/SidePanels/RightPanel.tsx';
import { Flex, Button } from '@mantine/core';
import { useDownloadFileUsingFileStorageUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadFileUsingFileStorageUrl';
import { useTranslationDocuments } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useEffect, useState } from 'react';
import { useGetDocument } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/hooks/useGetDocument';
import { updateDocumentLibraryResolver } from '@dealstack/frontend/app/pages/Project/DocumentLibrary/SidePanels/validationSchema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DocumentLibraryService } from '@dealstack/frontend/shared/dataExchange/services/document-library/document-library.service';
import { FormField } from '@dealstack/frontend/shared/components/Documents/FormFields/types';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { RelatedToModal } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/RelatedToModal/RelatedToModal';
import { DOCUMENT_LIBRARY_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';

interface Props {
	isRightSidePanelOpen: boolean;
	setRightPanelAction: (action: RightPanelAction) => void;
	dealId: string;
	selectedDocument: SelectedDocument;
}

export const EditDocument = ({
	isRightSidePanelOpen,
	setRightPanelAction,
	dealId,
	selectedDocument,
}: Props) => {
	const tDocuments = useTranslationDocuments();
	const { downloadFile } = useDownloadFileUsingFileStorageUrl();
	const queryClient = useQueryClient();
	const formState = useForm<GetDocumentRes['documentDetails']>({
		resolver: updateDocumentLibraryResolver,
	});
	const [editFieldKey, setEditFieldKey] = useState('');
	const [fieldAction, setFieldAction] = useState<DocumentFormAction | null>(null);

	const { data: documentData, isLoading: isDocumentDataLoading } = useGetDocument({
		dealId,
		selectedDocumentId: selectedDocument.documentId,
	});

	useEffect(() => {
		if (documentData) {
			formState.reset(documentData.documentDetails);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentData]);

	// Leaving edit mode belongs to "a different document was selected", not to "data arrived".
	// Resetting it on every documentData identity change closed the open field whenever a
	// background refetch landed — which the investor-visibility toggle triggers on each flip.
	useEffect(() => {
		setEditFieldKey('');
		setFieldAction(null);
	}, [selectedDocument.documentId]);

	const handleEditField = (fieldKey: string, action?: DocumentFormAction) => {
		setEditFieldKey(fieldKey);
		if (action) {
			setFieldAction(action);
		}
	};

	const onCancel = () => {
		formState.reset();
		setEditFieldKey('');
		setFieldAction(null);
	};

	const updateDocument = useMutation({
		mutationFn: DocumentLibraryService.updateDocument,
	});

	const onSave = async (field: FormField) => {
		const fieldData = formState.getValues(
			field.fieldKey as keyof GetDocumentRes['documentDetails']
		);
		updateDocument.mutate(
			{
				dealId,
				documentId: selectedDocument.documentId,
				updateData: { [field.fieldKey]: fieldData },
			},
			{
				onSuccess: async () => {
					setEditFieldKey('');
					await queryClient.refetchQueries({
						queryKey: [
							formatUnicorn(
								API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENT_DOCUMENTID_PUT,
								{
									dealId,
									documentId: selectedDocument.documentId,
								}
							),
						],
					});

					// refresh document data
					await queryClient.refetchQueries({
						queryKey: [
							formatUnicorn(
								API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENT_DOCUMENTID_GET,
								{
									dealId,
									documentId: selectedDocument.documentId,
								}
							),
						],
					});

					// Reload the documents table. Prefix key match refetches every
					// filter variant, so the row reflects the update regardless of
					// the table's active filters (the exact-key form missed it — it
					// omitted excludeWorkflowGenerated).
					await queryClient.refetchQueries({
						queryKey: [
							formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENTS_GET, {
								dealId,
							}),
						],
					});
				},
				onError: () => {
					showToastV2({ state: 'error', message: 'Failed to save data' });
				},
			}
		);
	};

	/**
	 * The modal refetches the deal-directory document queries itself; the library reads its own
	 * `document-library/*` endpoints, so the panel and the table need refreshing separately.
	 */
	const refetchLibraryDocument = async () => {
		await queryClient.refetchQueries({
			queryKey: [
				formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENT_DOCUMENTID_GET, {
					dealId,
					documentId: selectedDocument.documentId,
				}),
			],
		});

		await queryClient.refetchQueries({
			queryKey: [
				formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENTS_GET, {
					dealId,
				}),
			],
		});
	};

	// Mirrors DrawerBody: the same DocumentFormAction drives the same modal, so the library and the
	// deal directory manage transaction links through one surface.
	const toggleRelatedToModal = () => {
		setFieldAction((previous) =>
			previous === DocumentFormAction.OPEN_LINK_MODAL
				? null
				: DocumentFormAction.OPEN_LINK_MODAL
		);
	};

	if (isDocumentDataLoading) {
		return (
			<RightPanel isOpened={isRightSidePanelOpen}>
				<InlineLoader />
			</RightPanel>
		);
	}

	const relatedPeople = documentData?.documentDetails.relatedPeople || [];

	return (
		<>
			<Header>
				<HeaderTitle data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.EDIT_PANEL_TITLE}>
					{documentData?.documentDetails.documentTitle}
				</HeaderTitle>
				<HeaderCloseBtn
					data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.EDIT_PANEL_CLOSE}
					onClick={() => setRightPanelAction(RightPanelAction.NONE)}
				>
					<Icon variant="close-v3" />
				</HeaderCloseBtn>
			</Header>

			<FormProvider<GetDocumentRes['documentDetails']> {...formState}>
				<form
					style={{ height: '100%' }}
					onSubmit={(e) => {
						e.preventDefault();
					}}
				>
					<Flex direction="column" justify="space-between" h="100%">
						<DocumentFieldView
							fields={documentData?.meta.fields}
							formState={formState}
							dealId={dealId}
							documentId={selectedDocument.documentId}
							relatedPeople={relatedPeople}
							documentDetails={documentData?.documentDetails}
							isLoading={updateDocument?.isPending}
							editFieldKey={editFieldKey}
							fieldAction={fieldAction}
							onCancel={onCancel}
							onSave={onSave}
							handleEditField={handleEditField}
							control={formState.control}
						/>
						<Flex direction="column" gap="0.5rem">
							<Button
								data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.EDIT_PANEL_DOWNLOAD_BUTTON}
								onClick={() =>
									downloadFile({
										fileId: selectedDocument.fileId,
										dealId,
										fileName: selectedDocument.documentName,
									})
								}
							>
								{tDocuments('right_panel.download_document.button_text')}
							</Button>
							<Button
								data-testid={
									DOCUMENT_LIBRARY_ELEMENT_ID.EDIT_PANEL_MANAGE_TRANSACTIONS_BUTTON
								}
								variant="subtle"
								onClick={toggleRelatedToModal}
							>
								{tDocuments('right_panel.related_transactions.button_text')}
							</Button>
							<Button
								data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.EDIT_PANEL_DELETE_BUTTON}
								onClick={() =>
									setRightPanelAction(RightPanelAction.ARCHIVE_DOCUMENT)
								}
								variant="subtle"
								color="red"
							>
								{tDocuments('right_panel.delete_document.button_text')}
							</Button>
						</Flex>
					</Flex>
				</form>
			</FormProvider>

			{/*
			 * No entityId: the library lists documents deal-wide, so there is no transaction to
			 * anchor on. The backend derives the anchors from the document's own links instead.
			 */}
			<RelatedToModal
				dealId={dealId}
				editDocument={{ documentId: selectedDocument.documentId }}
				isOpen={fieldAction === DocumentFormAction.OPEN_LINK_MODAL}
				toggleModal={toggleRelatedToModal}
				onLinksSaved={refetchLibraryDocument}
			/>
		</>
	);
};
